import React from 'react';
import { Link } from 'react-router-dom';

function ForgotPasswordForm({ onBack }) {
    const [email, setEmail] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState('');
    const [success, setSuccess] = React.useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess(false);

        // Validate email
        if (!email) {
            setError('Please enter your email address');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('Please enter a valid email address');
            return;
        }

        setIsLoading(true);

        try {
            if (!window.authService) {
                throw new Error('Authentication service is not available');
            }
            
            await window.authService.resetPassword(email);
            setSuccess(true);
        } catch (err) {
            console.error('Password reset error:', err);
            setError(err.message || 'Failed to send password reset email. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    if (success) {
        return (
            <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-8">
                <div className="text-center">
                    <div className="text-green-500 text-5xl mb-4">
                        <i className="fas fa-check-circle"></i>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">Password Reset Link Sent</h2>
                    <p className="text-gray-600 mb-6">
                        We've sent a password reset link to <span className="font-semibold">{email}</span>. 
                        Please check your email and follow the instructions to reset your password.
                    </p>
                    <p className="text-gray-500 mb-6 text-sm">
                        If you don't see the email, please check your spam folder or try again.
                    </p>
                    <button 
                        onClick={() => onBack()}
                        className="btn btn-primary"
                    >
                        Back to Login
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-8">
            <h2 className="text-3xl font-bold text-center mb-8 text-gray-800">Forgot Password</h2>
            
            {error && (
                <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg flex items-center">
                    <i className="fas fa-exclamation-circle mr-2"></i>
                    <span>{error}</span>
                </div>
            )}

            <p className="text-gray-600 mb-6">
                Enter your email address and we'll send you a link to reset your password.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                    <label htmlFor="email" className="block text-gray-700 mb-2 font-medium">
                        Email <span className="text-red-500">*</span>
                    </label>
                    <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        required
                        placeholder="Enter your email"
                        autoComplete="email"
                        style={{ color: '#374151', backgroundColor: '#FFFFFF' }}
                    />
                </div>

                <button
                    type="submit"
                    disabled={isLoading}
                    className="btn btn-primary w-full"
                >
                    {isLoading ? (
                        <>
                            <i className="fas fa-spinner fa-spin mr-2"></i>
                            Sending Reset Link...
                        </>
                    ) : (
                        'Send Reset Link'
                    )}
                </button>
            </form>

            <div className="mt-6 text-center">
                <button 
                    onClick={() => onBack()}
                    className="text-green-600 hover:text-green-700 font-medium"
                >
                    Back to Login
                </button>
            </div>
        </div>
    );
}

export default ForgotPasswordForm;
